import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { StatsSkeleton } from '@/components/PageSkeleton';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils/date';
import { DEFS } from '@/canvas/defs/index';
import { useCohort } from '@/components/canvas/cohortData';
import { ProgressBar } from '@/components/canvas/bits';
import { SQUARE, StatusSquares } from '@/components/canvas/StatusSquares';

export const route = { path: '/cohort', layout: 'owner', access: "profile === 'Facilitator_c'" };
export const nav = { icon: 'Users', label: 'Cohort', order: 10, profiles: ['Facilitator_c'] };

function Tile({ icon, tint, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-xs">
      <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-xl text-white', tint)}>
        <ApperIcon name={icon} size={20} />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl font-semibold leading-tight tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

const WEEK = 7 * 24 * 3600 * 1000;

export default function Cohort() {
  const { learners, loading, error, reload } = useCohort();
  const [query, setQuery] = useState('');

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return learners;
    return learners.filter((l) => `${l.name} ${l.organisation}`.toLowerCase().includes(q));
  }, [learners, query]);

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6" aria-busy="true">
        <Skeleton className="h-9 w-48" />
        <StatsSkeleton count={4} />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl space-y-4 rounded-2xl border border-destructive/40 bg-destructive/10 p-6">
        <h1 className="font-heading text-xl font-semibold">The cohort did not load</h1>
        <p className="text-sm">{error}</p>
        <Button onClick={reload}>Try again</Button>
      </div>
    );
  }

  const ready = learners.filter((l) => l.ready).length;
  const active = learners.filter((l) => l.lastChange && Date.now() - new Date(l.lastChange).getTime() < WEEK).length;
  const avg = learners.length ? Math.round(learners.reduce((a, l) => a + l.overall, 0) / learners.length) : 0;
  const totalGaps = learners.reduce((a, l) => a + l.agg.gaps.length, 0);

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="space-y-1">
        <h1 className="font-heading text-3xl font-semibold text-balance">Cohort</h1>
        <p className="text-muted-foreground">Every learner who has started their canvases, with the same progress they see themselves.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile icon="Users" tint="bg-(color:--step-1)" label="Learners" value={learners.length} sub="with at least one canvas" />
        <Tile icon="FileCheck" tint="bg-(color:--step-3)" label="Packs complete" value={`${ready} of ${learners.length}`} sub="all ten confirmed" />
        <Tile icon="Gauge" tint="bg-(color:--step-4)" label="Average progress" value={`${avg}%`} sub="across the cohort" />
        <Tile icon="Activity" tint="bg-(color:--step-5)" label="Active this week" value={active} sub={`${totalGaps} Evidence Gaps logged`} />
      </div>

      {learners.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <span className="flex size-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
            <ApperIcon name="UsersRound" size={24} />
          </span>
          <h2 className="font-heading text-lg font-semibold">No learners yet</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Learners appear here as soon as they register and type their first answer. Ask them to register from the home page with their work email.
          </p>
        </div>
      ) : (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-semibold">Learners</h2>
            <div className="relative w-full sm:w-72">
              <ApperIcon name="Search" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or organisation"
                aria-label="Search learners"
                className="pl-9"
              />
            </div>
          </div>

          {shown.length === 0 ? (
            <p className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">No learner matches that search.</p>
          ) : (
            <>
              <div className="hidden overflow-x-auto rounded-2xl border border-border bg-card shadow-xs md:block">
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                      <th scope="col" className="px-4 py-3 font-semibold">Learner</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Canvases 0 to 9</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Progress</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Last change</th>
                      <th scope="col" className="px-4 py-3"><span className="sr-only">Open</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((l) => (
                      <tr key={l.id} className="border-t border-border align-middle">
                        <td className="px-4 py-3">
                          <p className="font-medium">{l.name}</p>
                          <p className="text-xs text-muted-foreground">{l.organisation || 'Organisation not given'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <StatusSquares states={l.states} />
                        </td>
                        <td className="min-w-40 px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ProgressBar value={l.overall} label={`${l.name} progress`} />
                            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{l.overall}%</span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {l.confirmed} of {DEFS.length} confirmed{l.stale > 0 ? `, ${l.stale} to review` : ''}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">{l.lastChange ? formatRelativeTime(l.lastChange) : 'Not yet'}</td>
                        <td className="px-4 py-3 text-right">
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/cohort/${l.id}`}>
                              View <ApperIcon name="ArrowRight" size={14} />
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="space-y-3 md:hidden">
                {shown.map((l) => (
                  <li key={l.id}>
                    <Link
                      to={`/cohort/${l.id}`}
                      className="block space-y-3 rounded-2xl border border-border bg-card p-4 shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium">{l.name}</p>
                          <p className="text-xs text-muted-foreground">{l.organisation || 'Organisation not given'}</p>
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground">{l.overall}%</span>
                      </div>
                      <StatusSquares states={l.states} />
                      <p className="text-xs text-muted-foreground">
                        {l.confirmed} of {DEFS.length} confirmed
                        {l.lastChange ? `. Last change ${formatRelativeTime(l.lastChange)}.` : '.'}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}

          <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="Key">
            {['Confirmed', 'In progress', 'Review', 'Not started'].map((k) => (
              <li key={k} className="flex items-center gap-1.5">
                <span className={cn('size-3 rounded-sm', SQUARE[k])} /> {k}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
